<template>
  <div>
    <ColorSwatches
      :id
      v-model="selectedColorId"
      :name="id"
      v-bind="props.colorProps"
      :show-dropdown="shouldDisplayColorPicker || memberFeatureEligible?.shouldDisplayColorPicker"
      :is-expandable="isMcom"
      :color-label="props.colorLabel"
      :is-colorway-pricing-copy="isColorwayPricingCopyEnabled"
      :is-mobile="isMobile"
      :is-scroll-color-swatch-into-view="killswitches.isScrollColorSwatchIntoViewEnabled"
      :is-custom-dropdown-for-color-swatches="killswitches.isCustomDropdownForColorSwatches && beautyFlag"
      :is-colorway-pricing="killswitches.isColorwayPricing1"
      :is-colorway-pricing2="killswitches.isColorwayPricing2"
      @update:model-value="fireTag"
    >
      <template #before-title>
        <div v-if="isFindationApplicable" data-el="findation" data-testid="findation" class="findation-container margin-bottom-xs opacity-z">
          <div id="findation-widget-button" ref="findationButton"></div>
        </div>
      </template>

      <template #before-swatches>
        <div v-if="shouldShowShadeText" class="findation-message-container small margin-bottom-xs margin-top-xs">
          <div class="findation-selection-confirmation confirmation-filled-dark-medium"></div>
          <span class="findation-selection-msg"> This shade is your <strong>best match</strong>. </span>
        </div>
      </template>

      <template #swatch-icon="{ color }">
        <span v-if="bestShadeColorId === color.id" class="findation-selected confirmation-filled-dark-medium"></span>
      </template>
    </ColorSwatches>

    <div
      v-if="
        !isStickyAtbSlideoutContextEnabled &&
        (isBTCustomColorSiteletEligible ||
          memberFeatureEligible?.isBTCustomColorSiteletEligible ||
          orderSwatches ||
          memberFeatureEligible?.orderSwatches)
      "
      class="buttons-container margin-top-xs"
    >
      <ViewCustomColors v-if="isBTCustomColorSiteletEligible || memberFeatureEligible?.isBTCustomColorSiteletEligible" />
      <OrderSwatches v-if="orderSwatches || memberFeatureEligible?.orderSwatches" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { ColorSwatches } from '@nuxt-common/media-card-large/app/modules/color';
import { useBrand } from '~/composables/useBrand';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import useExperiments from '~/stores/useExperimentsStore';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useFindation from '~/modules/color/composables/useFindation';
import ViewCustomColors from '../../components/viewCustomColors/ViewCustomColors.vue';
import useColor from '~/modules/color/composables/useColor';
import OrderSwatches from '~/modules/order-swatches/components/OrderSwatches.vue';
import tagColorChange from '~/composables/analytics/color';
import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';
import { useStickyAtbSlideoutContextEnabled } from '~/modules/sticky-product-traits/composables/context-providers/useStickyAtbSlideoutContextEnabled';

const props = defineProps({
  id: {
    type: [String, Number], // root level product id -> Number; member product ids -> String
    default: '',
  },
  colorProps: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  colorLabel: {
    type: String,
    required: false,
    default: 'Color',
  },
});

const { killswitches } = useKillswitches();
const {
  experimentFlags: { isColorwayPricingCopyEnabled },
} = useExperiments();

const { selectedColorId } = useColor();
const { beautyFlag } = useProductFlags();
const bestShadeColorId = ref(null);
const findationButton = ref(null);
const consolidatedStore = useConsolidatedMemberStore();
const { memberFeatureEligible } = storeToRefs(consolidatedStore);

const { isMcom } = useBrand();
const { isMobile } = useDeviceType();
const { isStickyAtbSlideoutContextEnabled } = useStickyAtbSlideoutContextEnabled();

const shouldShowShadeText = computed(() => isMcom && bestShadeColorId.value !== null && bestShadeColorId.value === selectedColorId.value);

const findationCallback = (matchingColor: string) => {
  if (matchingColor && props.colorProps?.colors) {
    /** @type {{id: number} | undefined}  */
    const match = props.colorProps.colors.find((color) => {
      const normalize = (str) => str.toLowerCase().replace(/[\s\-,.]/g, '');
      const normalizedColor = normalize(color?.name ?? '');
      const normalizedSearch = normalize(matchingColor);

      return normalizedColor.includes(normalizedSearch);
    });
    if (match?.id) {
      bestShadeColorId.value = match.id;
      selectedColorId.value = match.id;
    }
  }
};

const { initFindation, isFindationApplicable } = useFindation(findationCallback);

const { isBTCustomColorSiteletEligible, shouldDisplayColorPicker, orderSwatches } = useFeatureEligibility();
const { fireTag } = tagColorChange();

watch(
  [findationButton, isFindationApplicable],
  ([button, applicable]) => {
    if (button && applicable) {
      initFindation(button);
    }
  },
  { immediate: true },
);
</script>




<style lang="scss" scoped>
@import '~settingSCSS';


@import '~@csgIcons/new/confirmation-filled-dark-medium';

.findation-container.opacity-z {
  opacity: 0;
}
.findation-message-container {
  display: flex;
  flex-flow: row nowrap;
}
.findation-selection-confirmation {
  margin-right: 5px;
  width: 16px;
  height: 16px;
  background-size: contain;
}
.findation-selected {
  position: absolute;
  left: 51%;
  top: 15%;
  width: 16px;
  height: 16px;
  background-size: contain;
  z-index: 2;
}
.buttons-container {
  display: flex;
}
</style>